import Axios from "../../../lib/Axios";

/**
 * User Banner API Helper
 * 
 * API functions for user banner page
 */

/**
 * Get user banner images list
 * @param {Object} filters - Filter parameters
 * @param {string} filters.status - Status filter (pending, approved, rejected)
 * @param {string} filters.banner_id - Banner ID filter
 * @param {string} filters.moderation_result - Moderation result filter (pass, fail)
 * @param {string} filters.dateRange - Date range filter (last_24_hours, last_7_days, last_30_days)
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @returns {Promise} API response with banner images array and pagination
 */
export const getUserBannerList = async (filters = {}) => {
  const {
    status,
    banner_id,
    moderation_result,
    dateRange,
    startDate,
    endDate,
    limit = 50,
    page = 1,
  } = filters;

  // Build query parameters object
  const params = {
    limit: limit.toString(),
    page: page.toString(),
  };
  
  if (status) params.status = status;
  if (banner_id) params.banner_id = banner_id;
  if (moderation_result) params.moderation_result = moderation_result;
  if (dateRange && !startDate && !endDate) {
    params.dateRange = dateRange;
  }
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  console.log("API Params:", params);

  // GET /admin/user-banner-images
  const result = await Axios.get("admin/user-banner-images", { params });
  const banners = result?.data?.result?.banner_images || result?.data?.result?.banners || [];
  const pagination = result?.data?.result?.pagination || {};
  
  return {
    data: banners,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Approve a banner image
 * @param {string} banner_id - The banner ID to approve
 * @returns {Promise} API response
 */
export const approveBanner = async (banner_id) => {
  if (!banner_id) {
    throw new Error("banner_id is required");
  }

  // POST /admin/banner-images/{banner_id}/approve
  const result = await Axios.post(`admin/banner-images/${banner_id}/approve`);
  
  return result;
};

/**
 * Reject a banner image
 * @param {Object} data - Reject data
 * @param {string} data.banner_id - The banner ID to reject
 * @param {Object} data.rejection_reason - Rejection reason object
 * @param {string} data.rejection_reason.by_admin - Admin ID
 * @param {string} data.rejection_reason.timestamp - Timestamp
 * @param {Array<string>} data.rejection_reason.reasons - Array of rejection reasons
 * @param {string} data.rejection_reason.comment - Rejection comment
 * @returns {Promise} API response
 */
export const rejectBanner = async (data) => {
  const { banner_id, rejection_reason } = data;
  
  if (!banner_id) {
    throw new Error("banner_id is required");
  }
  if (!rejection_reason) {
    throw new Error("rejection_reason is required");
  }

  // POST /admin/banner-images/{banner_id}/reject
  const result = await Axios.post(`admin/banner-images/${banner_id}/reject`, {
    rejection_reason,
  });
  
  return result;
};

